import { useMutation, useQuery, UseQueryOptions } from 'react-query';
import { queryCreate, queryGet } from './fetch';
import { SessionFormat, StudySubject, StudyType } from './session';
import { Product } from './plan';
import { useMemo } from 'react';
import { useCurrentOrganizationId } from './currentOrganization';
import { Recurrence } from './subscription';

export interface PriceTier {
  id: string;
  minimum: number;
  maximum?: number;
  enabled: boolean;
  updatedAt: Date;
  createdAt: Date;
}

export interface OfferRequirement {
  studySubjects: StudySubject[];
  studyTypes: StudyType[];
  formats?: SessionFormat[];
  priceThierId?: string;
}

export enum OfferType {
  TARGETING = 'TARGETING',
  SUPPORT = 'SUPPORT',
  OPTION = 'OPTION',
  SUBSCRIPTION = 'SUBSCRIPTION',
  CREDIT = 'CREDIT',
  SPECIAL_OFFER = 'SPECIAL_OFFER',
}

export interface OfferProduct {
  id: string;
  product?: Product;
  productId: string;
  offetId: string;
  forRecruitmentByClient: boolean;
  countryPriceGroupId?: string;
  createdAt: Date;
}

export enum OfferColors {
  GREEN = 'green',
  BLUE = 'blue',
  PURPLE = 'purple',
  YELLOW = 'yellow',
  RED = 'red',
}

export interface DetailsPrices {
  eur: number;
  perParticipant: number;
  perParticipantPerHour: number;
  monthly: number;
  yearly: number;
  incentive: number;
  incentivePerHour: number;
}

export interface Offer {
  id: string;
  internalName: string;
  name: string;
  shortName?: string;
  description?: string;
  products: OfferProduct[];
  color?: OfferColors;
  type: OfferType;
  requirement: OfferRequirement;
  criteria?: string[];
  disabled: boolean;
  displayed: boolean;
  tooltip?: string;
  defaultSubscription?: boolean;
  requireCallToSales?: boolean;
  bookForCallLink?: string;
  mostPopular?: boolean;
  updatedAt: Date;
  createdAt: Date;
  prices: DetailsPrices;
  advantageAmount?: number;
}

export const useOffers = () => {
  return useQuery<Offer[]>(['offers'], queryGet('/v2/offers'));
};

export const usePriceTiers = (options?: UseQueryOptions<PriceTier[]>) => {
  return useQuery<PriceTier[]>(
    ['price-thiers'],
    queryGet('/v2/price-thiers'),
    options,
  );
};

export const useOffer = (id?: string) => {
  return useQuery<Offer>(['offers', id], queryGet(`/v2/offers/${id}`), {
    enabled: !!id,
  });
};

export const usePlanOffers = (id?: string) => {
  return useQuery<Offer[]>(
    ['plans', id, 'offers'],
    queryGet(`/v2/plans/${id}/offers`),
    {
      enabled: !!id,
    },
  );
};

export const useTargetingPlanOffer = (id?: string) => {
  const { data: offers } = usePlanOffers(id);

  return useMemo(() => {
    return offers?.find((offer) => offer.type === OfferType.TARGETING);
  }, [offers]);
};

/**
 * WIP
 */
export const useSubscribeToOfferUrl = () => {
  const organisationId = useCurrentOrganizationId();

  return useMutation<
    { url: string },
    Error,
    { offerId: string; recurrence: Recurrence }
  >(queryCreate(`/v2/organizations/${organisationId}/subscriptions/checkout`));
};
